var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { presetPrimaryColors } from '@ant-design/colors';
import { computed, defineComponent } from 'vue';
import { progressProps } from './props';
import { getSize, getSuccessPercent, validProgress } from './utils';
import { LineStrokeColorVar, Percent } from './style';
import devWarning from '../vc-util/devWarning';
import { anyType, objectType, stringType } from '../_util/type';
export const lineProps = () => (Object.assign(Object.assign({}, progressProps()), { strokeColor: anyType(), direction: stringType(), percentPosition: objectType() }));
/**
 * {
 *   '0%': '#afc163',
 *   '75%': '#009900',
 *   '50%': 'green',     ====>     '#afc163 0%, #66FF00 25%, #00CC00 50%, #009900 75%, #ffffff 100%'
 *   '25%': '#66FF00',
 *   '100%': '#ffffff'
 * }
 */
export const sortGradient = (gradients) => {
    let tempArr = [];
    Object.keys(gradients).forEach(key => {
        const formattedKey = parseFloat(key.replace(/%/g, ''));
        if (!isNaN(formattedKey)) {
            tempArr.push({
                key: formattedKey,
                value: gradients[key],
            });
        }
    });
    tempArr = tempArr.sort((a, b) => a.key - b.key);
    return tempArr.map(({ key, value }) => `${value} ${key}%`).join(', ');
};
/**
 * Then this man came to realize the truth: Besides six pence, there is the moon. Besides bread and
 * butter, there is the bug. And... Besides women, there is the code.
 *
 * @example
 *   {
 *     "0%": "#afc163",
 *     "25%": "#66FF00",
 *     "50%": "#00CC00", // ====>  linear-gradient(to right, #afc163 0%, #66FF00 25%,
 *     "75%": "#009900", //        #00CC00 50%, #009900 75%, #ffffff 100%)
 *     "100%": "#ffffff"
 *   }
 */
export const handleGradient = (strokeColor, directionConfig) => {
    const { from = presetPrimaryColors.blue, to = presetPrimaryColors.blue, direction = directionConfig === 'rtl' ? 'to left' : 'to right' } = strokeColor, rest = __rest(strokeColor, ["from", "to", "direction"]);
    if (Object.keys(rest).length !== 0) {
        const sortedGradients = sortGradient(rest);
        const background = `linear-gradient(${direction}, ${sortedGradients})`;
        return { background, [LineStrokeColorVar]: background };
    }
    const background = `linear-gradient(${direction}, ${from}, ${to})`;
    return { background, [LineStrokeColorVar]: background };
};
export default defineComponent({
    compatConfig: { MODE: 3 },
    name: 'ProgressLine',
    inheritAttrs: false,
    props: lineProps(),
    setup(props, { slots, attrs }) {
        const backgroundProps = computed(() => {
            const { strokeColor, direction } = props;
            return strokeColor && typeof strokeColor !== 'string'
                ? handleGradient(strokeColor, direction)
                : {
                    [LineStrokeColorVar]: strokeColor,
                    background: strokeColor,
                };
        });
        const borderRadius = computed(() => props.strokeLinecap === 'square' || props.strokeLinecap === 'butt' ? 0 : undefined);
        const trailStyle = computed(() => ({
            backgroundColor: props.trailColor || undefined,
            borderRadius: borderRadius.value,
        }));
        const mergedSize = computed(() => { var _a; return (_a = props.size) !== null && _a !== void 0 ? _a : [-1, props.strokeWidth || (props.size === 'small' ? 6 : 8)]; });
        const sizeRef = computed(() => getSize(mergedSize.value, 'line', { strokeWidth: props.strokeWidth }));
        if (process.env.NODE_ENV !== 'production') {
            devWarning('strokeWidth' in props, 'Progress', '`strokeWidth` is deprecated. Please use `size` instead.');
        }
        const percentStyle = computed(() => {
            const { percent } = props;
            return Object.assign(Object.assign({ width: `${validProgress(percent)}%`, height: `${sizeRef.value.height}px`, borderRadius: borderRadius.value }, backgroundProps.value), { [Percent]: validProgress(percent) / 100 });
        });
        const successPercent = computed(() => {
            return getSuccessPercent(props);
        });
        const successPercentStyle = computed(() => {
            const { success } = props;
            return {
                width: `${validProgress(successPercent.value)}%`,
                height: `${sizeRef.value.height}px`,
                borderRadius: borderRadius.value,
                backgroundColor: success === null || success === void 0 ? void 0 : success.strokeColor,
            };
        });
        const outerStyle = computed(() => ({
            width: sizeRef.value.width < 0 ? '100%' : sizeRef.value.width,
        }));
        const percentPosition = computed(() => {
            var _a, _b, _c, _d;
            return ({
                align: (_b = (_a = props.percentPosition) === null || _a === void 0 ? void 0 : _a.align) !== null && _b !== void 0 ? _b : 'end',
                type: (_d = (_c = props.percentPosition) === null || _c === void 0 ? void 0 : _c.type) !== null && _d !== void 0 ? _d : 'outer',
            });
        });
        return () => {
            var _a;
            const { prefixCls } = props;
            const { align: infoAlign, type: infoPosition } = percentPosition.value;
            const children = (_a = slots.default) === null || _a === void 0 ? void 0 : _a.call(slots);
            const lineInner = (<div class={`${prefixCls}-inner`} style={trailStyle.value}>
          <div class={[`${prefixCls}-bg`, `${prefixCls}-bg-${infoPosition}`]} style={percentStyle.value}>
            {infoPosition === 'inner' ? children : null}
          </div>
          {successPercent.value !== undefined ? (<div class={`${prefixCls}-success-bg`} style={successPercentStyle.value}/>) : null}
        </div>);
            const isOuterStart = infoPosition === 'outer' && infoAlign === 'start';
            const isOuterEnd = infoPosition === 'outer' && infoAlign === 'end';
            if (infoPosition === 'outer' && infoAlign === 'center') {
                return (<div {...attrs} class={[`${prefixCls}-layout-bottom`, attrs.class]}>
            {lineInner}
            {children}
          </div>);
            }
            return (<div {...attrs} class={[`${prefixCls}-outer`, attrs.class]} style={[attrs.style, outerStyle.value]}>
          {isOuterStart ? children : null}
          {lineInner}
          {isOuterEnd ? children : null}
        </div>);
        };
    },
});
